import t from 'react-translate';
import { useSelector } from 'react-redux';
import { useMentoringProgramQuestionsAnswers } from 'athena/hooks/use-mentoring-program-questions-answers';
import { useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { ProgramProfileQuestionType } from 'redux/actions/mentoring-program-profile-questions';
import { MentorshipProgramProfileAnswer } from 'redux/schemas/models/mentoring-program-profile-answers';
import { css } from '@emotion/react';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { ProgramLevelQuestion } from 'redux/schemas/models/mentoring-program-profile-questions';
import { config } from '@config/pendo.config.json';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import { getMentoringProgramEnrollment } from 'redux/selectors/mentoring-program-enrollments';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { sortQuestionByQuestionIndex } from 'athena/helpers/utils';
import { QuestionsProps } from '../types';
import Question from '../question';
import MentorCapacityQuestion from './default-questions/mentor-capacity-question';

const styles = css`
  .program-level-fields {
    gap: ${standardSpacing}px;
  }
`;

const ProgramQuestions = ({
  enrollmentId,
  mentorshipProgramId,
}: QuestionsProps) => {
  const { ref, inView: isEndReached } = useInView();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { isMentor } = useMentoringProgramRole();
  const { mentorCapacityEnabled, concurrentMentorConnection: maxMentees } = mentoringProgram || {};

  const {
    questions,
    loadMore,
  }: {
    questions: MentorshipProgramProfileAnswer[];
    loadMore: () => void;
  } = useMentoringProgramQuestionsAnswers({
    type: ProgramProfileQuestionType.PROGRAM_LEVEL,
    mentorshipProgramId,
    enrollmentId,
  });

  const programEnrollment = useSelector(state => getMentoringProgramEnrollment(state, enrollmentId));

  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.steps.profile
    .questions.programLevel;

  const renderMentorCapacityQuestion = isMentor && mentorCapacityEnabled;

  const questionsSortedByQuestionIndex = useMemo(() => (
    Object.values(questions || {}).sort(sortQuestionByQuestionIndex)
  ), [questions]);

  const programLevelFields = useMemo(
    () => (
      <div className='d-flex flex-column program-level-fields'>
        {renderMentorCapacityQuestion && (
          <MentorCapacityQuestion
            enrollmentId={enrollmentId}
            maxMentees={maxMentees}
          />
        )}
        {questionsSortedByQuestionIndex.map((question) => {
          const { id, isRequired, profileQuestion, answer, mentorQuestion } = question;
          const { questionList } = profileQuestion as ProgramLevelQuestion;
          const { questionText, type, responseOptions } = questionList || {};
          const participantQuestion = (
            programEnrollment?.role?.name === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR
            && !!mentorQuestion
          ) ? mentorQuestion
            : questionText;
          return (
            <Question
              id={id}
              key={id}
              type={type}
              answer={answer}
              title={participantQuestion}
              isRequired={isRequired}
              options={responseOptions.map((option) => option.optionContent)}
              dataQa={dataQATag}
              dataQaId={`${dataQATag}_${id}`}
            />
          );
        })}
      </div>
    ),
    [questionsSortedByQuestionIndex],
  );

  useEffect(() => {
    if (isEndReached) {
      loadMore();
    }
  }, [isEndReached, loadMore]);

  if (!questions?.length && !renderMentorCapacityQuestion) {
    return null;
  }

  return (
    <div css={styles}>
      <div className='heading-4 mt-5'>
        {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.PROGRAM_QUESTIONS.TITLE()}
      </div>
      <div className='text-body mt-1 mb-5'>
        {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.PROGRAM_QUESTIONS.DESCRIPTION()}
      </div>
      {programLevelFields}
      <div ref={ref} />
    </div>
  );
};

export default ProgramQuestions;
